// 고객 보드 — 고객마다 상세와 같은 여정 판정(journeyPlaceOf)으로 열을 정하고, 완료·이탈은 최근 30일만 싣는다.

import type { PillTone, StatusTone } from "../common/tone";
import { formatDate, formatWon } from "../../lib/format";
import type { Contract } from "../contract/rows";
import { CONTRACT_STATUS_LABEL } from "../contract/types";
import { CONTRACT_STATUS_TONE } from "../common/tone";
import {
  journeyPlaceOf,
  quoteActivityAt,
  siteProgressOf,
  type JourneyContext,
  type JourneyContract,
  type JourneyProject,
  type JourneyQuote,
} from "./journey";
import { customerTodos, headStageOf, type HeadStage, type TodoPortfolio } from "./journey-panel";
import type { CustomerStageEvent } from "./stage-events";
import { CUSTOMER_STAGE_LABEL, WORK_SCOPE_LABEL, type Customer } from "./types";
import type { Portfolio } from "../portfolio/types";
import type { Project, ProjectPhase } from "../project/types";
import { dDayLabel, dDayOf, projectNameOf } from "../project/view";
import { QUOTE_KIND_LABEL, QUOTE_STATUS_LABEL, type Quote } from "../quote/types";
import { QUOTE_STATUS_TONE } from "../common/tone";

/* ── 저장소가 읽는 열 ─────────────────────────────────────── */

export type BoardQuote = JourneyQuote & Pick<Quote, "customer_id" | "total">;
export const BOARD_QUOTE_COLUMNS = "id, customer_id, quote_no, kind, version, status, total, created_at, sent_at, decided_at";

// replaces_contract_id 는 유입 분석(갈아 낀 옛 계약 빼기)이 쓴다.
export type BoardContract = JourneyContract & Pick<Contract, "customer_id" | "total_amount" | "terminated_at" | "replaces_contract_id">;
export const BOARD_CONTRACT_COLUMNS =
  "id, customer_id, quote_id, contract_no, status, total_amount, created_at, signed_at, terminated_at, replaces_contract_id";

export type BoardProject = JourneyProject & Pick<Project, "customer_id" | "title" | "site_address">;
export const BOARD_PROJECT_COLUMNS =
  "id, customer_id, contract_id, status, title, site_address, created_at, planned_start_date, planned_end_date, started_at, completion_date, warranty_end_date, closed_at";

// 이탈로 옮긴 단계 이력 — 이탈일 판정에 시각만 쓴다.
export type BoardLostEvent = Pick<CustomerStageEvent, "id" | "customer_id" | "created_at">;
export const BOARD_LOST_EVENT_COLUMNS = "id, customer_id, created_at";

// 완료 현장의 시공사례 — 초안(이어 쓰기)·공개(보기) 판정에 id·공개 여부만 쓴다.
export type BoardPortfolio = TodoPortfolio & Pick<Portfolio, "project_id">;
export const BOARD_PORTFOLIO_COLUMNS = "id, project_id, published";

// Supabase 가 in() 에 싣는 고객 id 묶음 크기 — URL 한도를 넘지 않게.
export const BOARD_CUSTOMER_CHUNK = 100;

export type BoardCustomer = Pick<
  Customer,
  | "id"
  | "name"
  | "stage"
  | "created_at"
  | "site_address"
  | "area_band"
  | "work_scope"
  | "assignee_id"
  | "lost_reason"
>;

// 보드 카드가 쓰는 고객 열만 — 조직 전체 고객을 읽으므로 메모·주소 상세 같은 긴 열은 싣지 않는다.
export const BOARD_CUSTOMER_COLUMNS = "id, name, stage, created_at, site_address, area_band, work_scope, assignee_id, lost_reason";

export type BoardPhase = Pick<ProjectPhase, "project_id" | "status">;

// 인메모리 저장소가 Supabase 와 같은 열만 돌려주게 — 전체 행에서 보드 열만 떼어 낸다.
export const toBoardQuote = (q: Quote): BoardQuote => ({
  id: q.id,
  customer_id: q.customer_id,
  quote_no: q.quote_no,
  kind: q.kind,
  version: q.version,
  status: q.status,
  total: q.total,
  created_at: q.created_at,
  sent_at: q.sent_at,
  decided_at: q.decided_at,
});

export const toBoardContract = (c: Contract): BoardContract => ({
  id: c.id,
  customer_id: c.customer_id,
  quote_id: c.quote_id,
  contract_no: c.contract_no,
  status: c.status,
  total_amount: c.total_amount,
  created_at: c.created_at,
  signed_at: c.signed_at,
  terminated_at: c.terminated_at,
  replaces_contract_id: c.replaces_contract_id,
});

export const toBoardProject = (p: Project): BoardProject => ({
  id: p.id,
  customer_id: p.customer_id,
  contract_id: p.contract_id,
  status: p.status,
  title: p.title,
  site_address: p.site_address,
  created_at: p.created_at,
  planned_start_date: p.planned_start_date,
  planned_end_date: p.planned_end_date,
  started_at: p.started_at,
  completion_date: p.completion_date,
  warranty_end_date: p.warranty_end_date,
  closed_at: p.closed_at,
});

export const toBoardPortfolio = (p: Portfolio): BoardPortfolio => ({ id: p.id, project_id: p.project_id, published: p.published });

export const toBoardCustomer = (c: Customer): BoardCustomer => ({
  id: c.id,
  name: c.name,
  stage: c.stage,
  created_at: c.created_at,
  site_address: c.site_address,
  area_band: c.area_band,
  work_scope: c.work_scope,
  assignee_id: c.assignee_id,
  lost_reason: c.lost_reason,
});

export const toBoardLostEvent = (e: CustomerStageEvent): BoardLostEvent => ({ id: e.id, customer_id: e.customer_id, created_at: e.created_at });

// 고객 묶음의 행만 · 만든 순(Supabase 정렬과 같다).
export const rowsOfCustomers = <T extends { customer_id: string; created_at: string; id: string }>(
  rows: readonly T[],
  customerIds: readonly string[]
): T[] => {
  const wanted = new Set(customerIds);
  return rows
    .filter((r) => wanted.has(r.customer_id))
    .sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id));
};

/* ── 열 ────────────────────────────────────────────────────── */

export const BOARD_COLUMNS = ["INQUIRY", "QUOTE", "CONTRACT", "SCHEDULED", "IN_PROGRESS", "WARRANTY", "CLOSED", "LOST"] as const;
export type BoardColumnKey = (typeof BOARD_COLUMNS)[number];

// 가견적·상세견적은 한 열(견적) — 이름은 여정 단계·고객 단계 표기를 그대로 쓴다.
export const BOARD_COLUMN_LABEL: Record<BoardColumnKey, string> = {
  INQUIRY: "문의",
  QUOTE: CUSTOMER_STAGE_LABEL.QUOTED,
  CONTRACT: "계약",
  SCHEDULED: "시공대기",
  IN_PROGRESS: "시공중",
  WARRANTY: "유지보수",
  CLOSED: "완료",
  LOST: CUSTOMER_STAGE_LABEL.LOST,
};

// 여정 자리(0~7) → 열.
const COLUMN_AT: readonly BoardColumnKey[] = ["INQUIRY", "QUOTE", "QUOTE", "CONTRACT", "SCHEDULED", "IN_PROGRESS", "WARRANTY", "CLOSED"];

// 최근 30일만 싣는 열 — 「더 보기」(?all=CLOSED,LOST) 로 열마다 따로 펼친다.
export type RecentColumnKey = "CLOSED" | "LOST";
export const RECENT_ONLY_COLUMNS: readonly BoardColumnKey[] = ["CLOSED", "LOST"];
export const BOARD_RECENT_DAYS = 30;

export const asBoardColumn = (value: string | null | undefined): BoardColumnKey | null =>
  (BOARD_COLUMNS as readonly string[]).includes(value ?? "") ? (value as BoardColumnKey) : null;

// ?all= 값(쉼표로 여럿 · 같은 키 여러 번도) → 아는 열만 · 중복 없이 · 열 순서.
export const asRecentColumns = (value: string | readonly string[] | null | undefined): RecentColumnKey[] => {
  const given = new Set((typeof value === "string" ? [value] : (value ?? [])).flatMap((v) => v.split(",")).map((v) => v.trim()));
  return (RECENT_ONLY_COLUMNS as readonly string[]).filter((key) => given.has(key)) as RecentColumnKey[];
};

// 펼친 열 목록 → ?all= 값(없으면 null) · 열 순서로 한 번씩.
export const recentParam = (cols: readonly RecentColumnKey[]): string | null => asRecentColumns(cols).join(",") || null;

/* ── 카드 ──────────────────────────────────────────────────── */

export type BoardBadge = { kind: "status"; tone: StatusTone; text: string } | { kind: "tag"; tone: PillTone; text: string };

export interface BoardCard {
  id: string;
  name: string;
  href: string;
  owner: string;
  meta: string;
  amount: number | null;
  progress: { pct: number; text: string } | null;
  badges: BoardBadge[];
  next: { label: string; href: string } | null;
  // 열에 들어온 날(KST) — 완료·이탈은 30일 기준일, 나머지는 「오래 머문 순」 기준.
  since: string | null;
}

export interface BoardColumn {
  key: BoardColumnKey;
  title: string;
  cards: BoardCard[];
  // 30일 밖 카드 수 — 펼치지 않았고 0 보다 크면 「더 보기」.
  hidden: number;
  // ?all= 로 30일 자르지 않고 전부 실었는가.
  expanded: boolean;
  moreHref: string | null;
  lessHref: string | null;
}

export interface CustomerBoard {
  columns: BoardColumn[];
  total: number;
}

export interface BoardInput {
  customers: readonly BoardCustomer[];
  quotes: readonly BoardQuote[];
  contracts: readonly BoardContract[];
  projects: readonly BoardProject[];
  // 시공중 현장의 공정 — null 이면 읽기 실패라 진행 막대를 그리지 않는다.
  phases: readonly BoardPhase[] | null;
  // null 이면 읽기 실패 — 이탈 고객은 재시작으로 보지 않는다(이탈 열 유지).
  lostEvents: readonly BoardLostEvent[] | null;
  memberName: ReadonlyMap<string, string>;
  areaLabel: ReadonlyMap<string, string>;
  // 이탈 사유 코드 → 표기(codes.lost_reason) — 없는 코드는 코드 그대로.
  lostLabel: ReadonlyMap<string, string>;
  // 완료 현장의 시공사례 — null 이면 읽기 실패라 상세처럼 「사례 만들기」 로 둔다.
  portfolios: readonly BoardPortfolio[] | null;
  today: string;
  expanded: readonly RecentColumnKey[];
  // 그 열을 펼친/접은 주소 — 다른 열의 펼침은 그대로 둔다.
  moreHref: Record<RecentColumnKey, string>;
  lessHref: Record<RecentColumnKey, string>;
}

const DAY_MS = 86_400_000;
const dayOf = (value: string | null | undefined): string | null => (value ? formatDate(value, "") || null : null);
const daysBetween = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
const shiftDay = (day: string, delta: number): string => new Date(Date.parse(`${day}T00:00:00Z`) + delta * DAY_MS).toISOString().slice(0, 10);
// 올해면 월-일만.
const shortDay = (day: string, today: string): string => (day.slice(0, 4) === today.slice(0, 4) ? day.slice(5) : day);
const latestDay = (values: readonly (string | null | undefined)[]): string | null =>
  values.map(dayOf).filter((d): d is string => d !== null).sort().pop() ?? null;

const groupBy = <T extends { customer_id: string }>(rows: readonly T[]): Map<string, T[]> => {
  const out = new Map<string, T[]>();
  for (const row of rows) out.set(row.customer_id, [...(out.get(row.customer_id) ?? []), row]);
  return out;
};

interface Placed {
  column: BoardColumnKey;
  ctx: JourneyContext;
  project: BoardProject | null;
  // 이탈 뒤 다시 견적 중이면 상세 머리 뱃지와 같은 보조 표시(재문의).
  note: string | null;
}

// 상세 화면과 같은 기준 — 진행 중 현장 → 살아 있는 새 영업 → 가장 최근 현장.
const placeOf = (
  customer: BoardCustomer,
  quotes: BoardQuote[],
  contracts: BoardContract[],
  projects: BoardProject[],
  lastLostAt: string | null
): Placed => {
  const place = journeyPlaceOf(customer, quotes, contracts, projects, lastLostAt);
  const { ctx, project } = place;
  return { column: ctx.lost ? "LOST" : COLUMN_AT[ctx.pos], ctx, project, note: headStageOf(customer.stage, place)?.note ?? null };
};

// 이탈한 날 — 해지·이탈 결정·마지막 이탈 전환 이력 중 가장 늦은 날(셋 다 없으면 null = 30일 밖).
const lostDayOf = (ctx: JourneyContext, contractById: ReadonlyMap<string, BoardContract>, lastLostAt: string | null): string | null =>
  latestDay([
    ctx.live?.status === "TERMINATED" ? (contractById.get(ctx.live.id)?.terminated_at ?? null) : null,
    ctx.series?.latest.status === "LOST" ? ctx.series.latest.decided_at : null,
    ctx.rough?.status === "LOST" ? ctx.rough.decided_at : null,
    lastLostAt,
  ]);

const sinceOf = (
  placed: Placed,
  customer: BoardCustomer,
  contractById: ReadonlyMap<string, BoardContract>,
  lastLostAt: string | null
): string | null => {
  const { column, ctx, project } = placed;
  switch (column) {
    case "INQUIRY":
      return dayOf(customer.created_at);
    case "QUOTE":
      return dayOf(ctx.series?.start ?? ctx.rough?.created_at);
    case "CONTRACT":
      return dayOf(ctx.live?.created_at ?? ctx.series?.latest.decided_at ?? ctx.series?.latest.created_at);
    case "SCHEDULED":
      return dayOf(project?.created_at ?? ctx.live?.signed_at ?? ctx.live?.created_at);
    case "IN_PROGRESS":
      return dayOf(project?.started_at ?? project?.created_at);
    case "WARRANTY":
      return dayOf(project?.completion_date ?? project?.created_at);
    case "CLOSED":
      return dayOf(project?.closed_at ?? project?.completion_date);
    case "LOST":
      return lostDayOf(ctx, contractById, lastLostAt);
  }
};

const metaOf = (customer: BoardCustomer, project: BoardProject | null, areaLabel: ReadonlyMap<string, string>, today: string): string => {
  if (!project) {
    return [
      customer.site_address,
      customer.area_band ? (areaLabel.get(customer.area_band) ?? customer.area_band) : null,
      customer.work_scope ? WORK_SCOPE_LABEL[customer.work_scope] : null,
    ]
      .filter(Boolean)
      .join(" · ");
  }
  const name = projectNameOf(project);
  const when =
    project.status === "SCHEDULED"
      ? project.planned_start_date
        ? `착공 ${shortDay(project.planned_start_date, today)}`
        : "착공일 미정"
      : project.status === "WARRANTY"
        ? project.warranty_end_date
          ? `보증 ~ ${shortDay(project.warranty_end_date, today)}`
          : null
        : project.status === "CLOSED"
          ? project.completion_date
            ? `준공 ${shortDay(project.completion_date, today)}`
            : null
          : null;
  return [name, when].filter(Boolean).join(" · ");
};

const quoteBadges = (q: JourneyQuote): BoardBadge[] => [
  ...(q.kind === "ROUGH" ? [{ kind: "tag" as const, tone: "neutral" as const, text: QUOTE_KIND_LABEL.ROUGH }] : []),
  { kind: "status", tone: QUOTE_STATUS_TONE[q.status], text: QUOTE_STATUS_LABEL[q.status] },
  ...(q.version > 1 ? [{ kind: "tag" as const, tone: "outline" as const, text: `v${q.version}` }] : []),
];

const badgesOf = (placed: Placed, customer: BoardCustomer, today: string, lostLabel: ReadonlyMap<string, string>): BoardBadge[] => {
  const { column, ctx, project } = placed;
  switch (column) {
    case "INQUIRY": {
      if (customer.stage === "CONTACTED") return [{ kind: "tag", tone: "progress", text: "연락함" }];
      if (customer.stage !== "NEW") return [];
      const created = dayOf(customer.created_at);
      const days = created ? daysBetween(created, today) : 0;
      return [days <= 0 ? { kind: "tag", tone: "new", text: "오늘 접수" } : { kind: "tag", tone: "hold", text: `연락 전 ${days}일` }];
    }
    case "QUOTE": {
      const q = ctx.series?.latest ?? ctx.rough;
      return q ? quoteBadges(q) : [];
    }
    case "CONTRACT":
      return ctx.live
        ? [{ kind: "status", tone: CONTRACT_STATUS_TONE[ctx.live.status], text: CONTRACT_STATUS_LABEL[ctx.live.status] }]
        : [{ kind: "tag", tone: "neutral", text: "계약서 전" }];
    case "SCHEDULED": {
      if (!project) return [{ kind: "tag", tone: "hold", text: "현장 만들기 전" }];
      const days = dDayOf(project.planned_start_date, today);
      return days === null ? [] : [{ kind: "tag", tone: days < 0 ? "danger" : "new", text: dDayLabel(days) }];
    }
    case "LOST":
      return customer.lost_reason ? [{ kind: "tag", tone: "dropped", text: lostLabel.get(customer.lost_reason) ?? customer.lost_reason }] : [];
    default:
      return [];
  }
};

const amountOf = (placed: Placed, quoteById: ReadonlyMap<string, BoardQuote>, contractById: ReadonlyMap<string, BoardContract>): number | null => {
  const { column, ctx } = placed;
  const live = ctx.live ? contractById.get(ctx.live.id) : undefined;
  const q = ctx.series?.latest ?? ctx.rough;
  const quoteTotal = q ? (quoteById.get(q.id)?.total ?? null) : null;
  switch (column) {
    case "QUOTE":
      return quoteTotal;
    case "CONTRACT":
    case "SCHEDULED":
      return live ? live.total_amount : quoteTotal;
    default:
      return null;
  }
};

const compareCards = (recentFirst: boolean) => (a: BoardCard, b: BoardCard): number => {
  if (a.since !== b.since) {
    if (a.since === null) return 1;
    if (b.since === null) return -1;
    return recentFirst ? b.since.localeCompare(a.since) : a.since.localeCompare(b.since);
  }
  return a.name.localeCompare(b.name) || a.id.localeCompare(b.id);
};

// 카드 링크의 접근성 이름 — 겹쳐 덮는 링크라 카드 안 글자(담당·뱃지·금액)를 이름에 싣는다.
export const cardLabel = (card: BoardCard): string =>
  [
    card.name,
    `담당 ${card.owner}`,
    card.meta,
    card.progress ? [card.progress.text, `${card.progress.pct}%`].filter(Boolean).join(" · ") : "",
    ...card.badges.map((b) => b.text),
    card.amount !== null ? formatWon(card.amount) : "",
  ]
    .filter(Boolean)
    .join(", ");

// 카드를 그리기 전의 배치 — 열·열에 들어온 날·30일 안(또는 펼친 열)이라 보이는가.
export type BoardLayoutInput = Pick<BoardInput, "customers" | "quotes" | "contracts" | "projects" | "lostEvents" | "today" | "expanded">;

interface Laid {
  customer: BoardCustomer;
  placed: Placed;
  since: string | null;
  inWindow: boolean;
  shown: boolean;
}

// 이탈 이력을 못 읽은 이탈 고객 — 마지막 견적 활동 때 이탈했다고 보아 그 뒤 재시작이 없게 한다.
export const unknownLossAt = (quotes: readonly Pick<BoardQuote, "created_at" | "sent_at">[]): string | null =>
  quotes.map(quoteActivityAt).reduce<string | null>((a, b) => (a === null || Date.parse(b) > Date.parse(a) ? b : a), null);

// 고객마다 가장 늦은 이탈 전환 이력 시각.
const lastLostAtOf = (lostEvents: readonly BoardLostEvent[] | null): Map<string, string> => {
  const out = new Map<string, string>();
  for (const e of lostEvents ?? []) {
    const prev = out.get(e.customer_id);
    if (!prev || prev < e.created_at) out.set(e.customer_id, e.created_at);
  }
  return out;
};

// 판정에 넣을 이탈 시각 — 이력을 못 읽은(null) 이탈 고객은 마지막 견적 활동으로 본다.
const placeLostOf = (customer: BoardCustomer, quotes: readonly BoardQuote[], lostEvents: readonly BoardLostEvent[] | null, lost: string | null): string | null =>
  lostEvents === null && customer.stage === "LOST" ? unknownLossAt(quotes) : lost;

const layoutOf = (input: BoardLayoutInput): Laid[] => {
  const quotesOf = groupBy(input.quotes);
  const contractsOf = groupBy(input.contracts);
  const projectsOf = groupBy(input.projects);
  const contractById = new Map(input.contracts.map((c) => [c.id, c]));
  const cutoff = shiftDay(input.today, -(BOARD_RECENT_DAYS - 1));
  const lastLostAt = lastLostAtOf(input.lostEvents);
  return input.customers.map((customer): Laid => {
    const lost = lastLostAt.get(customer.id) ?? null;
    const quotes = quotesOf.get(customer.id) ?? [];
    const placeLost = placeLostOf(customer, quotes, input.lostEvents, lost);
    const placed = placeOf(customer, quotes, contractsOf.get(customer.id) ?? [], projectsOf.get(customer.id) ?? [], placeLost);
    const since = sinceOf(placed, customer, contractById, lost);
    const old = RECENT_ONLY_COLUMNS.includes(placed.column) && (since === null || since < cutoff);
    return { customer, placed, since, inWindow: !old, shown: !old || input.expanded.includes(placed.column as RecentColumnKey) };
  });
};

// 기본 목록 단계 칸의 재문의 표시 — 이탈 고객마다 보드와 같은 판정(재시작 아니면 빠진다).
export const restartedHeadsOf = (
  input: Pick<BoardInput, "customers" | "quotes" | "contracts" | "projects" | "lostEvents">
): Map<string, HeadStage> => {
  const quotesOf = groupBy(input.quotes);
  const contractsOf = groupBy(input.contracts);
  const projectsOf = groupBy(input.projects);
  const lastLostAt = lastLostAtOf(input.lostEvents);
  const out = new Map<string, HeadStage>();
  for (const customer of input.customers) {
    if (customer.stage !== "LOST") continue;
    const quotes = quotesOf.get(customer.id) ?? [];
    const lost = placeLostOf(customer, quotes, input.lostEvents, lastLostAt.get(customer.id) ?? null);
    const place = journeyPlaceOf(customer, quotes, contractsOf.get(customer.id) ?? [], projectsOf.get(customer.id) ?? [], lost);
    const head = headStageOf(customer.stage, place);
    if (head) out.set(customer.id, head);
  }
  return out;
};

// 보드에 보이는 완료 카드의 현장 — 시공사례는 이 현장만 읽는다(30일 밖 접힌 카드는 버튼을 그리지 않는다).
export const visibleClosedProjectIds = (input: BoardLayoutInput): string[] =>
  layoutOf(input).flatMap(({ shown, placed }) =>
    shown && placed.column === "CLOSED" && placed.project?.status === "CLOSED" ? [placed.project.id] : []
  );

// 열 여덟 개 — 빈 열도 그대로 둔다. 완료·이탈은 최근 30일(오늘 포함, KST)만 싣되 펼친 열(expanded)은 전부, 최근 순. 나머지는 오래 머문 순.
export const buildCustomerBoard = (input: BoardInput): CustomerBoard => {
  const { today } = input;
  const quoteById = new Map(input.quotes.map((q) => [q.id, q]));
  const contractById = new Map(input.contracts.map((c) => [c.id, c]));
  const portfolioOf = new Map((input.portfolios ?? []).map((p) => [p.project_id, p]));

  const buckets = new Map<BoardColumnKey, BoardCard[]>(BOARD_COLUMNS.map((key) => [key, []]));
  const hidden = new Map<BoardColumnKey, number>(BOARD_COLUMNS.map((key) => [key, 0]));

  for (const { customer, placed, since, inWindow, shown } of layoutOf(input)) {
    if (!inWindow) hidden.set(placed.column, (hidden.get(placed.column) ?? 0) + 1);
    if (!shown) continue;

    const project = placed.project;
    const portfolio = project?.status === "CLOSED" ? (portfolioOf.get(project.id) ?? null) : null;
    const todo = customerTodos({
      ctx: placed.ctx,
      project,
      changeOrders: [],
      contactPending: customer.stage === "NEW",
      today,
      portfolio,
    })[0];
    const action = todo?.action ?? null;
    const href = `/app/customers/${customer.id}`;
    const progress =
      placed.column === "IN_PROGRESS" && project && input.phases
        ? siteProgressOf(
            project,
            input.phases.filter((p) => p.project_id === project.id),
            today
          )
        : null;

    buckets.get(placed.column)!.push({
      id: customer.id,
      name: customer.name,
      href,
      owner: customer.assignee_id ? (input.memberName.get(customer.assignee_id) ?? "미지정") : "미지정",
      meta: metaOf(customer, project, input.areaLabel, today),
      amount: amountOf(placed, quoteById, contractById),
      progress,
      badges: [...badgesOf(placed, customer, today, input.lostLabel), ...(placed.note ? [{ kind: "tag" as const, tone: "neutral" as const, text: placed.note }] : [])],
      next: action ? { label: action.label, href: action.kind === "link" ? action.href : href } : null,
      since,
    });
  }

  const columns = BOARD_COLUMNS.map((key): BoardColumn => {
    const recent = RECENT_ONLY_COLUMNS.includes(key);
    const expanded = recent && input.expanded.includes(key as RecentColumnKey);
    const more = hidden.get(key) ?? 0;
    return {
      key,
      title: BOARD_COLUMN_LABEL[key],
      cards: [...(buckets.get(key) ?? [])].sort(compareCards(recent)),
      hidden: more,
      expanded,
      moreHref: recent && !expanded && more > 0 ? input.moreHref[key as RecentColumnKey] : null,
      lessHref: expanded ? input.lessHref[key as RecentColumnKey] : null,
    };
  });
  return { columns, total: input.customers.length };
};
